import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  RiMapPinLine, 
  RiMailLine, 
  RiPhoneLine, 
  RiBuildingLine,
  RiNavigationLine,
  RiCalendarEventLine,
  RiCarLine,
  RiTrainLine,
  RiBusLine,
  RiParkingLine,
  RiWifiLine,
  RiComputerLine,
  RiRestaurantLine,
  RiCupLine,
  RiShieldCheckLine,
  Ri24HoursLine,
  RiWheelchairLine,
  RiArrowRightLine,
  RiStarLine,
  RiCommunityLine,
  RiHotelLine,
  RiTaxiLine,
  RiPresentationLine,
  RiRefreshLine,
  RiErrorWarningLine,
  RiCheckLine,
  RiTimeLine,
} from 'react-icons/ri';
import toast from 'react-hot-toast';
import { API_BASE } from '../config';
import { useParams } from 'react-router-dom';
import { useSummit } from './summitdetails';

/* ─── THEME COLORS ─────────────────────────────────────────────────────────────── */
const T = {
  white: "#FFFFFF",
  offWhite: "#F4F9F8",
  softTeal: "#E8F5F3",
  primary: "#2A9D8F",
  primaryDark: "#1F7A6E",
  primaryLight: "#3BBCAE",
  navy: "#1A3A4A",
  navyDeep: "#0D2B3E",
  accent: "#3BBCAE",
  accentGold: "#D4A017",
  accentGoldDark: "#B8880F",
  accentGoldLight: "#E8B520",
  accentTealLight: "#6BC2B5",
  accentTealDark: "#218577",
  text: "#1C3461",
  textMid: "#2D4A5A",
  textMuted: "#6B8090",
  textLight: "#9BB0C0",
  border: "#C8E8E4",
  borderLight: "#E2F2F0",
  shadow: "0 4px 24px rgba(42,157,143,0.10)",
  shadowMd: "0 8px 40px rgba(42,157,143,0.14)",
  shadowLg: "0 20px 60px rgba(13,43,62,0.20)",
  shadowGold: "0 8px 32px rgba(212,160,23,0.25)",
  gradPrimary: "linear-gradient(135deg, #2A9D8F 0%, #3BBCAE 100%)",
  gradNavy: "linear-gradient(135deg, #0D2B3E 0%, #1A3A4A 100%)",
  gradSoft: "linear-gradient(135deg, #E8F5F3 0%, #F4F9F8 100%)",
  gradGold: "linear-gradient(135deg, #D4A017 0%, #E8B520 100%)",
  gradGoldReverse: "linear-gradient(135deg, #E8B520 0%, #D4A017 100%)",
  gradTealDark: "linear-gradient(135deg, #1F7A6E 0%, #2A9D8F 100%)",
};

/* ─── CSS with responsive rules ─────────────────────────────────────────────── */
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html{scroll-behavior:smooth}
  body,#root{background:${T.offWhite};color:${T.text};font-family:'Inter',sans-serif}
  
  ::-webkit-scrollbar{width:8px}
  ::-webkit-scrollbar-track{background:${T.borderLight}}
  ::-webkit-scrollbar-thumb{background:${T.primaryLight};border-radius:4px}

  .t-hero{background:${T.gradSoft};border-bottom:1px solid ${T.border};padding:clamp(40px,7vw,80px) clamp(16px,4vw,64px) clamp(32px,5vw,64px);position:relative;overflow:hidden}
  .t-hero::before{content:'';position:absolute;top:-120px;left:50%;transform:translateX(-50%);width:600px;height:600px;background:radial-gradient(circle,rgba(42,157,143,0.08) 0%,transparent 70%);pointer-events:none}
  .t-hero-i{max-width:1320px;margin:0 auto;display:flex;align-items:flex-start;justify-content:space-between;gap:40px;flex-wrap:wrap;position:relative}
  .t-hero-lbl{display:inline-flex;align-items:center;gap:8px;padding:5px 14px;border-radius:20px;background:${T.softTeal};border:1px solid ${T.border};color:${T.primary};font-size:11px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;margin-bottom:20px}
  .t-hero-h{font-family:'Playfair Display',serif;font-size:clamp(1.8rem,4.5vw,3.4rem);font-weight:900;color:${T.navy};letter-spacing:-.03em;line-height:1.06;margin-bottom:14px}
  .t-hero-h em{font-style:normal;color:${T.accentGold}}
  .t-hero-p{font-size:clamp(.85rem,1.3vw,1rem);color:${T.textMid};line-height:1.7;max-width:500px}
  .t-stats{display:flex;gap:1px;background:${T.border};border-radius:14px;border:1px solid ${T.border};overflow:hidden;align-self:flex-start;flex-shrink:0}
  .t-st{background:${T.white};padding:20px 28px;display:flex;flex-direction:column;gap:4px;min-width:100px}
  .t-st:nth-child(2){background:${T.offWhite}}
  .t-st-n{font-family:'Playfair Display',serif;font-size:1.8rem;font-weight:900;color:${T.accentGold};line-height:1;letter-spacing:-.03em}
  .t-st-l{font-size:11px;color:${T.textMuted};font-weight:500;text-transform:uppercase;letter-spacing:.08em}

  .t-card{background:${T.white};border-radius:20px;border:1px solid ${T.borderLight};padding:clamp(20px,3vw,28px);box-shadow:${T.shadow};transition:all 0.3s ease}
  .t-card:hover{transform:translateY(-6px);box-shadow:${T.shadowLg}}

  .t-amenity-item{display:flex;align-items:center;gap:10px;padding:8px 10px;background:${T.offWhite};border-radius:14px;transition:all 0.2s;cursor:default}
  .t-amenity-item:hover{background:${T.softTeal};transform:translateX(4px)}

  .t-transport-item{display:flex;align-items:center;gap:14px;background:${T.offWhite};padding:14px 16px;border-radius:20px;transition:all 0.2s}
  .t-transport-item:hover{background:${T.softTeal};transform:translateX(6px)}

  .t-hotel-item{display:flex;align-items:center;justify-content:space-between;background:${T.offWhite};padding:14px 18px;border-radius:20px;transition:all 0.2s}
  .t-hotel-item:hover{background:${T.softTeal};transform:translateX(6px)}

  .t-cta-w{padding:0 clamp(16px,4vw,64px) 48px;background:${T.offWhite}}
  .t-cta{max-width:1320px;margin:0 auto;background:${T.gradNavy};border:1px solid rgba(59,188,174,0.28);border-radius:20px;padding:clamp(24px,4vw,52px) clamp(24px,4vw,60px);display:flex;align-items:center;justify-content:space-between;gap:32px;flex-wrap:wrap;position:relative;overflow:hidden}
  .t-cta::before{content:'';position:absolute;inset:0;background:radial-gradient(circle at 50% 100%,rgba(212,160,23,0.12) 0%,transparent 60%);pointer-events:none}
  .t-cta-tag{display:inline-block;padding:4px 12px;border-radius:20px;background:rgba(212,160,23,0.15);border:1px solid rgba(212,160,23,0.25);color:${T.accentGoldLight};font-size:11px;letter-spacing:.08em;text-transform:uppercase;margin-bottom:12px;font-weight:700}
  .t-cta-h{font-family:'Playfair Display',serif;font-size:clamp(1.2rem,2.5vw,2rem);font-weight:900;color:${T.white};letter-spacing:-.025em;line-height:1.15}
  .t-cta-p{font-size:14px;color:${T.textMuted};margin-top:8px;line-height:1.6}
  .t-cta-r{display:flex;flex-direction:column;align-items:flex-end;gap:10px}
  .t-cta-btn{display:inline-flex;align-items:center;gap:8px;padding:13px 28px;background:${T.gradGold};color:${T.navyDeep};border-radius:50px;text-decoration:none;font-family:'Inter',sans-serif;font-size:14px;font-weight:700;transition:all .2s}
  .t-cta-btn:hover{background:${T.gradGoldReverse};transform:translateY(-2px);box-shadow:${T.shadowGold}}
  .t-cta-btn-outline{display:inline-flex;align-items:center;gap:8px;padding:13px 28px;background:transparent;border:1px solid rgba(255,255,255,0.3);color:${T.white};border-radius:50px;text-decoration:none;font-family:'Inter',sans-serif;font-size:14px;font-weight:700;transition:all .2s}
  .t-cta-btn-outline:hover{background:rgba(255,255,255,0.1);transform:translateY(-2px)}

  .t-footer{border-top:1px solid ${T.border};background:${T.white};padding:28px clamp(16px,4vw,64px);margin-top:40px}
  .t-fi{max-width:1320px;margin:0 auto;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:10px}
  .t-f-copy{font-size:12px;color:${T.textMuted};letter-spacing:.01em}

  .t-sw{max-width:1320px;margin:0 auto;display:flex;flex-direction:column;align-items:center;text-align:center;padding:72px 24px;gap:12px}
  .t-s-icon{width:52px;height:52px;border-radius:13px;background:${T.softTeal};border:1px solid ${T.border};display:flex;align-items:center;justify-content:center;font-size:22px;color:${T.primary};margin-bottom:4px}
  .t-s-title{font-family:'Playfair Display',serif;font-size:20px;font-weight:800;color:${T.navy}}
  .t-s-sub{font-size:14px;color:${T.textMuted};max-width:300px;line-height:1.6}
  .t-s-btn{margin-top:4px;padding:9px 20px;border-radius:8px;border:1px solid ${T.border};background:${T.white};color:${T.text};font-family:'Inter',sans-serif;font-size:13px;font-weight:500;cursor:pointer;transition:all .15s}
  .t-s-btn:hover{border-color:${T.primary};color:${T.primary}}

  /* ─── RESPONSIVE OVERRIDES ─── */
  @media (max-width: 1024px) {
    .t-hero-i { flex-direction: column; align-items: center; text-align: center; }
    .t-hero-p { max-width: 100%; margin: 0 auto; }
    .t-stats { align-self: center; }
    .venue-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
  }
  @media (max-width: 768px) {
    .t-hero { padding: 32px 16px 24px; }
    .t-hero-h { font-size: 2rem; }
    .t-hero-p { font-size: 0.9rem; }
    .t-stats { flex-direction: column; width: 100%; border-radius: 12px; }
    .t-st { padding: 16px; align-items: center; min-width: auto; width: 100%; }
    .t-st-n { font-size: 1.5rem; }
    .t-card { padding: 20px; }
    .t-cta { flex-direction: column; text-align: center; padding: 28px 20px; }
    .t-cta-r { width: 100%; align-items: center; }
    .t-cta-btn, .t-cta-btn-outline { width: 100%; justify-content: center; }
    .t-footer { padding: 20px 16px; }
    .t-fi { flex-direction: column; text-align: center; gap: 8px; }
    .venue-amenities { grid-template-columns: 1fr 1fr !important; }
    .venue-details-badge { flex-direction: column; align-items: stretch !important; gap: 12px !important; }
    .venue-transport-list { gap: 10px; }
    .venue-hotel-list { gap: 10px; }
    .venue-help-buttons { flex-direction: column; align-items: center; width: 100%; }
    .venue-help-buttons a { width: 100%; justify-content: center; }
    .venue-map { height: 220px !important; }
  }
  @media (max-width: 480px) {
    .t-hero { padding: 24px 12px 20px; }
    .t-hero-h { font-size: 1.6rem; }
    .t-hero-lbl { font-size: 10px; padding: 4px 12px; }
    .t-st-n { font-size: 1.3rem; }
    .t-card { padding: 16px; }
    .venue-amenities { grid-template-columns: 1fr !important; }
    .t-cta-h { font-size: 1.2rem; }
    .t-cta-p { font-size: 0.9rem; }
    .venue-help-card { padding: 24px 16px !important; }
    .venue-help-card h3 { font-size: 18px !important; }
    .venue-help-card p { font-size: 13px !important; }
  }

  @keyframes spin{to{transform:rotate(360deg)}}
`;

const Venue = () => {
  const { id: slugFromUrl } = useParams();
  const summitContext = useSummit();
  const eventId = summitContext?.eventId || slugFromUrl;

  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    console.log("🔍 Venue page - resolved eventId:", eventId);
    if (eventId) {
      fetchVenue();
    } else {
      setError("No event specified");
      setLoading(false);
    }
  }, [eventId]);

  const fetchVenue = async () => {
    try {
      setLoading(true);
      setError(null);
      const url = `${API_BASE}/api/venue/active/${eventId}`;
      console.log('📡 Fetching venue from:', url);
      
      const { data } = await axios.get(url);
      console.log("✅ Venue response:", data);

      if (data.success && data.venue) {
        setVenue(data.venue);
      } else {
        setError("Venue information not available for this event");
      }
    } catch (err) {
      console.error("❌ Error fetching venue:", err);
      if (err.response?.status === 404) {
        setError("Venue not found. Please check back later.");
      } else {
        setError("Failed to load venue information");
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ background: T.offWhite, minHeight: "100vh" }}>
        <style>{css}</style>
        <div className="t-hero">
          <div className="t-hero-i">
            <div>
              <div className="t-hero-lbl">
                <RiMapPinLine size={13} />
                Meetings21 · Venue
              </div>
              <h1 className="t-hero-h">Event <em>Venue</em></h1>
              <p className="t-hero-p">Loading venue information...</p>
            </div>
          </div>
        </div>
        <div className="t-sw">
          <div className="t-s-icon"><RiRefreshLine size={24} /></div>
          <p className="t-s-title">Loading Venue Details</p>
          <p className="t-s-sub">Please wait while we load the venue information...</p>
        </div>
      </div>
    );
  }

  if (error || !venue) {
    return (
      <div style={{ background: T.offWhite, minHeight: "100vh" }}>
        <style>{css}</style>
        <div className="t-hero">
          <div className="t-hero-i">
            <div>
              <div className="t-hero-lbl">
                <RiMapPinLine size={13} />
                Meetings21 · Venue
              </div>
              <h1 className="t-hero-h">Event <em>Venue</em></h1>
              <p className="t-hero-p">Discover our premier conference location</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 24 }}>
                <div style={{ flex: 1, height: 2, maxWidth: 80, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
                <div style={{ width: 8, height: 8, background: T.accentGold, transform: 'rotate(45deg)' }} />
                <div style={{ flex: 1, height: 2, maxWidth: 80, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
              </div>
            </div>
          </div>
        </div>
        <div className="t-sw">
          <div className="t-s-icon"><RiErrorWarningLine size={28} /></div>
          <p className="t-s-title">Venue Information Unavailable</p>
          <p className="t-s-sub">{error || "Please check back later for venue details."}</p>
          <button className="t-s-btn" onClick={fetchVenue}>Try Again</button>
          {eventId && (
            <p style={{ fontSize: 11, color: T.textMuted, marginTop: 8 }}>
              Event ID: {eventId}
            </p>
          )}
        </div>
      </div>
    );
  }

  const fullAddress = `${venue.address}, ${venue.area}, ${venue.city}, ${venue.state}${venue.pincode ? ` - ${venue.pincode}` : ''}`;
  const mapEmbedUrl = venue.mapEmbedUrl || `https://www.openstreetmap.org/export/embed.html?bbox=${venue.coordinates?.lng - 0.02}%2C${venue.coordinates?.lat - 0.02}%2C${venue.coordinates?.lng + 0.02}%2C${venue.coordinates?.lat + 0.02}&layer=mapnik&marker=${venue.coordinates?.lat}%2C${venue.coordinates?.lng}`;
  const googleMapsDestination = encodeURIComponent(`${venue.name}, ${fullAddress}`);

  return (
    <div style={{ background: T.offWhite, minHeight: "100vh" }}>
      <style>{css}</style>

      {/* ══════════ HERO ══════════ */}
      <section className="t-hero">
        <div className="t-hero-i">
          <div>
            <div className="t-hero-lbl">
              <RiMapPinLine size={13} />
              Meetings21 · Venue
            </div>
            <h1 className="t-hero-h">
              Event <em>Venue</em>
            </h1>
            <p className="t-hero-p">
              Join researchers and industry leaders at {venue.city}'s premier conference venue
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 24 }}>
              <div style={{ flex: 1, height: 2, maxWidth: 80, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
              <div style={{ width: 8, height: 8, background: T.accentGold, transform: 'rotate(45deg)' }} />
              <div style={{ flex: 1, height: 2, maxWidth: 80, background: `linear-gradient(90deg, transparent, ${T.primary}40, transparent)` }} />
            </div>
          </div>
          <div className="t-stats">
            <div className="t-st">
              <span className="t-st-n">{venue.capacity || 500}+</span>
              <span className="t-st-l">Capacity</span>
            </div>
            <div className="t-st">
              <span className="t-st-n">{venue.totalHalls || 4}</span>
              <span className="t-st-l">Halls</span>
            </div>
            <div className="t-st">
              <span className="t-st-n">{venue.parkingSpaces || 100}+</span>
              <span className="t-st-l">Parking</span>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ MAIN CONTENT ══════════ */}
      <div style={{ maxWidth: 1320, margin: '0 auto', padding: '40px clamp(16px,4vw,64px) 80px' }}>
        <div className="venue-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 32 }}>
          
          {/* ─── LEFT COLUMN - Venue Info Card ─── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="t-card"
            style={{ padding: 0, overflow: 'hidden' }}
          >
            <div style={{ background: T.gradPrimary, padding: '28px' }}>
              <h2 style={{ fontSize: 24, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12, color: T.white, fontWeight: 700 }}>
                <RiBuildingLine size={28} />
                Venue Information
              </h2>
              <p style={{ color: T.softTeal, fontSize: 14 }}>{venue.subtitle || "Complete conference details"}</p>
            </div>

            <div style={{ padding: '28px' }}>
              {/* Address */}
              <div style={{ background: T.offWhite, borderRadius: 20, padding: '24px', marginBottom: 28 }}>
                <h3 style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5, color: T.primary, marginBottom: 16 }}>📍 Address</h3>
                <p style={{ fontSize: 16, lineHeight: 1.6, color: T.text, marginBottom: 8, fontWeight: 600 }}>{venue.name}</p>
                <p style={{ fontSize: 14, color: T.textMid, lineHeight: 1.6 }}>{fullAddress}</p>
              </div>

              {/* Contact */}
              <div style={{ marginBottom: 28 }}>
                <h3 style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5, color: T.primary, marginBottom: 16 }}>📞 Contact</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {venue.contactPhone && (
                    <a href={`tel:${venue.contactPhone}`} style={{ display: 'flex', alignItems: 'center', gap: 14, textDecoration: 'none', color: T.textMid, fontSize: 15, padding: '10px 0', borderBottom: `1px solid ${T.borderLight}`, transition: 'all 0.2s' }}
                      onMouseEnter={e => { e.currentTarget.style.color = T.primary; e.currentTarget.style.transform = 'translateX(5px)'; }}
                      onMouseLeave={e => { e.currentTarget.style.color = T.textMid; e.currentTarget.style.transform = 'translateX(0)'; }}>
                      <RiPhoneLine size={18} style={{ color: T.primary }} />
                      <span>{venue.contactPhone}</span>
                    </a>
                  )}
                  {venue.contactEmail && (
                    <a href={`mailto:${venue.contactEmail}`} style={{ display: 'flex', alignItems: 'center', gap: 14, textDecoration: 'none', color: T.textMid, fontSize: 15, padding: '10px 0', borderBottom: `1px solid ${T.borderLight}`, transition: 'all 0.2s' }}
                      onMouseEnter={e => { e.currentTarget.style.color = T.primary; e.currentTarget.style.transform = 'translateX(5px)'; }}
                      onMouseLeave={e => { e.currentTarget.style.color = T.textMid; e.currentTarget.style.transform = 'translateX(0)'; }}>
                      <RiMailLine size={18} style={{ color: T.primary }} />
                      <span>{venue.contactEmail}</span>
                    </a>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '10px 0', color: T.textMid, fontSize: 15 }}>
                    <RiMapPinLine size={18} style={{ color: T.primary }} />
                    <span>{venue.area || venue.city}</span>
                  </div>
                </div>
              </div>

              {/* Operating Hours */}
              {venue.operatingHours && (
                <div style={{ marginBottom: 28 }}>
                  <h3 style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5, color: T.primary, marginBottom: 16 }}>⏰ Operating Hours</h3>
                  <div style={{ background: T.offWhite, borderRadius: 16, padding: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ fontSize: 13, color: T.textMid }}>Monday – Friday</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: T.primary }}>{venue.operatingHours.weekdays || "9:00 AM – 6:00 PM"}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ fontSize: 13, color: T.textMid }}>Saturday</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: T.primary }}>{venue.operatingHours.saturday || "10:00 AM – 2:00 PM"}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 13, color: T.textMid }}>Sunday</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: venue.operatingHours.sunday === "Closed" ? T.accentTealDark : T.primary }}>{venue.operatingHours.sunday || "Closed"}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Amenities */}
              {venue.amenities && venue.amenities.length > 0 && (
                <div>
                  <h3 style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5, color: T.primary, marginBottom: 16 }}>✨ Amenities & Facilities</h3>
                  <div className="venue-amenities" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                    {venue.amenities.sort((a, b) => a.order - b.order).map((amenity, idx) => (
                      <div key={idx} className="t-amenity-item">
                        <RiCheckLine size={14} style={{ color: T.primary }} />
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{amenity.name}</div>
                          {amenity.description && <div style={{ fontSize: 10, color: T.textMuted }}>{amenity.description}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          {/* ─── RIGHT COLUMN ─── */}
          <div>
            {/* Map Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="t-card"
              style={{ padding: 0, overflow: 'hidden', marginBottom: 28 }}
            >
              <div style={{ background: T.gradPrimary, padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <RiNavigationLine size={24} style={{ color: T.white }} />
                <h2 style={{ fontSize: 20, fontWeight: 600, color: T.white, margin: 0 }}>Location Map</h2>
              </div>
              <div className="venue-map" style={{ height: 320, width: '100%', background: T.offWhite }}>
                <iframe title="Venue Location Map" src={mapEmbedUrl} style={{ width: '100%', height: '100%', border: 0 }} allowFullScreen />
              </div>
              <div style={{ padding: '20px 24px', background: T.white, borderTop: `1px solid ${T.borderLight}` }}>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <a href={`https://www.google.com/maps/dir/?api=1&destination=${googleMapsDestination}`} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, background: T.gradGold, color: T.navyDeep, padding: '12px 28px', borderRadius: 40, textDecoration: 'none', fontSize: 14, fontWeight: 600, transition: 'all 0.2s', boxShadow: T.shadowGold }}
                    onMouseEnter={e => { e.currentTarget.style.opacity = '0.9'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = T.shadowLg; }}
                    onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = T.shadowGold; }}>
                    <RiNavigationLine size={18} /> Get Directions
                  </a>
                  <a href={`https://www.google.com/maps/place/${googleMapsDestination}`} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, background: T.offWhite, color: T.primary, padding: '12px 28px', borderRadius: 40, textDecoration: 'none', fontSize: 14, fontWeight: 600, border: `1px solid ${T.border}`, transition: 'all 0.2s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = T.primary; e.currentTarget.style.color = T.white; e.currentTarget.style.borderColor = T.primary; }}
                    onMouseLeave={e => { e.currentTarget.style.background = T.offWhite; e.currentTarget.style.color = T.primary; e.currentTarget.style.borderColor = T.border; }}>
                    Open in Google Maps
                  </a>
                </div>
              </div>
            </motion.div>

            {/* ══════════ VENUE INFO BADGES ───────────────────────────── */}
            <div style={{ 
              background: T.white, 
              borderRadius: 20, 
              border: `1px solid ${T.borderLight}`, 
              padding: '24px 28px', 
              marginBottom: 28,
              boxShadow: T.shadow 
            }}>
              <div className="venue-details-badge" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 4 }}>📍 Venue Details</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: `1px solid ${T.borderLight}` }}>
                  <RiBuildingLine size={18} style={{ color: T.primary }} />
                  <span style={{ fontSize: 14, color: T.text, fontWeight: 500 }}>{venue.name || "Olujhgf"}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: `1px solid ${T.borderLight}` }}>
                  <RiMapPinLine size={18} style={{ color: T.accentGold }} />
                  <span style={{ fontSize: 14, color: T.text }}>{venue.city || "Hyderabad"}, {venue.state || "Telangana"}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0' }}>
                  <RiWifiLine size={18} style={{ color: T.primary }} />
                  <span style={{ fontSize: 14, color: T.text }}>Free WiFi</span>
                </div>
              </div>
            </div>

            {/* Getting Here - Transport */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="t-card"
              style={{ padding: 0, overflow: 'hidden', marginBottom: 28 }}
            >
              <div style={{ background: T.gradPrimary, padding: '20px 24px' }}>
                <h2 style={{ fontSize: 20, fontWeight: 600, color: T.white, margin: 0 }}>Getting Here</h2>
              </div>
              <div style={{ padding: '24px' }}>
                {/* Airport Info */}
                {venue.airportInfo && (
                  <div style={{ marginBottom: 24 }}>
                    <h3 style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5, color: T.primary, marginBottom: 14 }}>✈️ Airport</h3>
                    <div style={{ background: T.offWhite, borderRadius: 20, padding: '16px' }}>
                      <p style={{ fontSize: 14, color: T.text, marginBottom: 6, fontWeight: 600 }}>{venue.airportInfo.name}</p>
                      <p style={{ fontSize: 13, color: T.textMuted, margin: 0 }}>
                        🚗 {venue.airportInfo.distance} • Approx. {venue.airportInfo.travelTime} drive {venue.airportInfo.route ? `via ${venue.airportInfo.route}` : ''}
                      </p>
                    </div>
                  </div>
                )}

                {/* Nearby Transport */}
                {venue.nearbyTransport && venue.nearbyTransport.length > 0 && (
                  <div style={{ marginBottom: 24 }}>
                    <h3 style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5, color: T.primary, marginBottom: 14 }}>🚍 Nearby Transport</h3>
                    <div className="venue-transport-list" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {venue.nearbyTransport.sort((a, b) => a.order - b.order).map((item, idx) => (
                        <div key={idx} className="t-transport-item">
                          {item.type === 'metro' ? <RiTrainLine size={22} style={{ color: T.primary }} /> :
                           item.type === 'bus' ? <RiBusLine size={22} style={{ color: T.primary }} /> :
                           <RiCarLine size={22} style={{ color: T.primary }} />}
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{item.name}</div>
                            <div style={{ fontSize: 12, color: T.textMuted }}>{item.distance} • {item.details}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Parking Info */}
                {venue.parkingInfo && (
                  <div style={{ background: T.softTeal, borderRadius: 20, padding: '16px 20px', borderLeft: `4px solid ${T.primary}` }}>
                    <p style={{ fontSize: 13, color: T.textMid, margin: 0, lineHeight: 1.5 }}>
                      <strong style={{ color: T.primary }}>💡 Parking Information:</strong><br />
                      {venue.parkingInfo}
                    </p>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Nearby Hotels */}
            {venue.nearbyHotels && venue.nearbyHotels.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="t-card"
                style={{ padding: 0, overflow: 'hidden', marginBottom: 28 }}
              >
                <div style={{ background: T.gradPrimary, padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <RiHotelLine size={22} style={{ color: T.white }} />
                  <h2 style={{ fontSize: 20, fontWeight: 600, color: T.white, margin: 0 }}>Nearby Accommodation</h2>
                </div>
                <div style={{ padding: '24px' }}>
                  <div className="venue-hotel-list" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {venue.nearbyHotels.sort((a, b) => a.order - b.order).map((hotel, idx) => (
                      <div key={idx} className="t-hotel-item">
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{hotel.name}</div>
                          <div style={{ fontSize: 12, color: T.textMuted }}>📍 {hotel.distance} from venue</div>
                          {hotel.rating && <div style={{ fontSize: 11, color: T.accentGold }}>⭐ {hotel.rating} ★</div>}
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: T.primary }}>{hotel.priceRange}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: 16, textAlign: 'center' }}>
                    <a href={`https://www.google.com/maps/search/hotels+near+${encodeURIComponent(venue.name + ', ' + venue.city)}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: T.primary, textDecoration: 'none', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      View more hotels nearby <RiArrowRightLine size={14} />
                    </a>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Need Assistance Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              style={{ background: T.gradPrimary, borderRadius: 32, overflow: 'hidden', boxShadow: T.shadowLg }}
            >
              <div className="venue-help-card" style={{ padding: '32px 28px', textAlign: 'center' }}>
                <div style={{ width: 56, height: 56, background: 'rgba(255,255,255,0.2)', borderRadius: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                  <RiCalendarEventLine size={28} style={{ color: T.white }} />
                </div>
                <h3 style={{ fontSize: 20, fontWeight: 700, color: T.white, marginBottom: 12 }}>Need Assistance?</h3>
                <p style={{ fontSize: 14, color: T.softTeal, marginBottom: 24, lineHeight: 1.5 }}>
                  Our venue coordinators are here to help you with any questions about the venue, directions, or accommodations.
                </p>
                <div className="venue-help-buttons" style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <a href={`mailto:${venue.contactEmail || 'info@meetings21.com'}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: T.gradGold, color: T.navyDeep, padding: '12px 28px', borderRadius: 40, textDecoration: 'none', fontSize: 14, fontWeight: 600, transition: 'all 0.2s', boxShadow: T.shadowGold }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = T.shadowLg; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = T.shadowGold; }}>
                    Email Support
                  </a>
                  <a href={`tel:${venue.contactPhone || '+919848141833'}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.15)', color: T.white, padding: '12px 28px', borderRadius: 40, textDecoration: 'none', fontSize: 14, fontWeight: 600, border: `1px solid rgba(255,255,255,0.3)`, transition: 'all 0.2s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.25)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                    Call Us
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Venue;